/**
 * Sorts error object keys by reordering 'firstname' and 'lastname' entries.
 *
 * This function ensures that 'firstname' appears before 'lastname' in the sorted object.
 * All other keys are left in their original order if they don't match 'firstname' or 'lastname'.
 *
 * @param {object} errorObj - The error object to be sorted. Keys should be strings.
 * @returns {Record<string, string>} A new object with reordered entries where 'firstname'
 * comes before 'lastname' when both exist.
 */
export function sortErrorObject(
  errorObj: object // Input error object with string keys
): Record<string, string> {
  // Convert the input object into an array of [key, value] pairs for easier manipulation.
  const entries: [string, string][] = Object.entries(errorObj);

  // Sort the entries based on custom logic:
  // - 'firstname' should come before 'lastname'.
  // - Other keys remain in their original relative order.
  const sortedEntries = entries.sort(([keyA], [keyB]) => {
    // Convert keys to lowercase for case-insensitive comparison.
    const keyALower = keyA.toLowerCase();
    const keyBLower = keyB.toLowerCase();

    // Check if keyA or keyB matches 'firstname' or 'lastname'.
    const aIsFirstName = keyALower.includes('firstname');
    const bIsLastName = keyBLower.includes('lastname');
    const aIsLastName = keyALower.includes('lastname');
    const bIsFirstName = keyBLower.includes('firstname');

    // Prioritize last name over first name while maintaining original order
    if (aIsFirstName && bIsLastName) return 1;
    if (aIsLastName && bIsFirstName) return -1;

    // If neither condition applies, keep the original order (return 0).
    return 0;
  });

  // Rebuild the object from the sorted entries array.
  return Object.fromEntries(sortedEntries);
}
